import React from 'react';
import style from './loyaltyPage.module.scss';
import { AppState, Gift } from 'acscp/store';
import {
    AcknowledgeLoyaltyTermsAndConditionsRequest,
    GiftsRequest,
    LoyaltyTermsAndConditionsMetaDataRequest,
    RequestClass
} from 'acscp/api/requests';
import { FetchRequest } from 'acscp/actions';
import { PresentationComponentBase } from 'acscp/components/presentationComponentBase';
import { Dispatch } from 'redux';
import { connectWithRequests } from 'acscp/store/helpers/connectWithRequests';
import { Background, Row } from 'acscp/components/common/row';
import { RequestStateProps } from 'acscp/components/common/helpers/requestStateProps';
import { FormattedMessage } from 'react-intl';
import { getCanAffordGifts, getCategorisedGifts, LoyaltyGiftCategory } from 'acscp/store/helpers/giftsHelper';
import { GiftCategory } from 'acscp/components/loyalty/giftCategory';
import { PageHeaderRow } from 'acscp/components/common/pageHeaderRow';
import classNames from 'classnames';
import { defineMessages, InjectedIntlProps, injectIntl } from 'react-intl';
import { BasketButton } from 'acscp/components/loyalty/basketButton';
import { Link } from 'react-router-dom';
import { LoyaltyTermsAndConditionsModal } from './loyaltyTermsAndConditionsModal';
import { ButtonWithLoadingIndicator } from 'acscp/components/common/buttonWithLoadingIndicator';
import { LoadingIndicator, LoadingIndicatorColour } from 'acscp/components/common/loadingIndicator';

const messages = defineMessages({
    availableGiftsWithCurrentBalanceTitle: {
        id: 'components.gifts.loyaltyPage.availableGiftsWithCurrentBalanceTitle',
        defaultMessage: 'GIFTS AVAILABLE WITH YOUR CURRENT POINTS TOTAL'
    },
    allGiftsFilterLabel: {
        id: 'components.gifts.loyaltyPage.allGifts',
        defaultMessage: 'All Gifts.'
    }
});

interface LoyaltyPageDispatchProps {
    fetchGifts: () => void;
    fetchLoyaltyTermsAndConditionsMetaData: () => void;
    acknowledgeLoyaltyTermsAndConditions: () => void;
}

interface LoyaltyPageStateProps {
    categories: LoyaltyGiftCategory[];
    canAffordCategoryGifts: Gift[];
    balance: number | null;
    mustAcknowledgeTermsAndConditions: boolean;
    isLoading: boolean;
}

interface LoyaltyPageState {
    categoryFilterId: string;
}

export class LoyaltyPagePresentational
    extends PresentationComponentBase<
        InjectedIntlProps,
        LoyaltyPageStateProps,
        LoyaltyPageDispatchProps,
        LoyaltyPageState> {
    private allGiftsCategoryFilterId: string = '';

    constructor(props: InjectedIntlProps & LoyaltyPageStateProps & LoyaltyPageDispatchProps) {
        super(props);
        this.state = { categoryFilterId: this.allGiftsCategoryFilterId };
    }

    componentDidMount(): void {
        this.props.fetchGifts();
        this.props.fetchLoyaltyTermsAndConditionsMetaData();
    }

    render(): JSX.Element {
        if (this.props.categories.length === 0) {
            return this.props.isLoading ? (
                <div className={style.InitialPageLoadingIndicator}>
                    <LoadingIndicator colour={LoadingIndicatorColour.Blue} />
                </div>
            ) : (
                <Row>
                    {this.props.mustAcknowledgeTermsAndConditions && this.renderTermsAndConditionsPopup()}
                    <FormattedMessage
                        id={'components.gifts.loyaltyPage.noItems'}
                        defaultMessage={'There are currently no available loyalty gifts.'}
                    />
                </Row>
            );
        }

        const categoryOptions = [(
            <option key={this.allGiftsCategoryFilterId} value={this.allGiftsCategoryFilterId}>
                {this.props.intl.formatMessage(messages.allGiftsFilterLabel)}
            </option>
        )].concat(this.props.categories.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
        )));
        const categoriesToRender = this.state.categoryFilterId === this.allGiftsCategoryFilterId
            ? this.props.categories
            : this.props.categories.filter(c => c.id === this.state.categoryFilterId);
        const showCanAffordCategory = this.props.canAffordCategoryGifts.length > 0;

        return (
            <div>
                {this.props.mustAcknowledgeTermsAndConditions && this.renderTermsAndConditionsPopup()}
                <PageHeaderRow
                    leftContent={(
                        <div className={classNames([style.HeaderRowTitle, style.LeftInfo])}>
                            <div className={style.BalanceInfo}>
                                {this.renderBalanceMessage()}
                                <button
                                    className={style.RefreshBalanceButton}
                                    onClick={this.props.fetchGifts}
                                    aria-label="Refresh page"
                                />
                            </div>
                            <Link
                                to={'/loyalty/transaction-history'}
                                className={style.TransactionHistoryButton}
                            >
                                <FormattedMessage
                                    id={'components.gifts.loyaltyPage.transactionHistory'}
                                    defaultMessage={'Transaction History'}
                                />
                            </Link>
                        </div>
                    )}
                    showLoadingIndicator={this.props.isLoading}
                    rightContent={(
                        <BasketButton openOnAddingGift={true} />
                    )}
                />

                {showCanAffordCategory
                    && (
                        <GiftCategory
                            category={{
                                id: this.allGiftsCategoryFilterId,
                                gifts: this.props.canAffordCategoryGifts,
                                name: this.props.intl.formatMessage(messages.availableGiftsWithCurrentBalanceTitle)
                            }}
                            isPlaceholder={this.props.isLoading}
                        />
                    )}

                <PageHeaderRow
                    leftContent={(
                        <p className={style.HeaderRowTitle}>
                            <FormattedMessage
                                id={'components.gifts.loyaltyPage.fullListOfGifts'}
                                defaultMessage={'FULL LIST OF ACS REWARD GIFTS'}
                            />
                        </p>
                        )}
                    showLoadingIndicator={this.props.isLoading}
                    rightContent={(
                        <p className={style.FilterContainer}>
                            <FormattedMessage
                                id={'components.gifts.loyaltyPage.filterGiftsLabel'}
                                defaultMessage={'Filter'}
                            />

                            <select onChange={v => this.setState({categoryFilterId: v.target.value})} className={style.Filter}>
                                {categoryOptions}
                            </select>
                        </p>
                    )}
                    background={showCanAffordCategory ? Background.LightestGrey : Background.Transparent}
                />

                {categoriesToRender.map((category, index) => (
                    <GiftCategory category={category} isPlaceholder={this.props.isLoading} key={category.id || index} />
                ))}
                {this.renderTermsAndConditions()}
            </div>
        );
    }

    private renderBalanceMessage(): JSX.Element {
        return this.props.balance != null
            ? (
                <FormattedMessage
                    id={'components.gifts.loyaltyPage.currentBalance'}
                    defaultMessage={`Your Current Points: {balance, number}`}
                    values={{
                        balance: this.props.balance
                    }}
                />
            ) : (
                <FormattedMessage
                    id={'components.gifts.loyaltyPage.balanceUnavailable'}
                    defaultMessage={`Balance unavailable`}
                />
            );
    }

    private renderTermsAndConditions(): JSX.Element {
        const linkForWordsClickHere = (
            <Link
                to="/loyalty/terms-and-conditions"
                className={style.TsAndCsLink}
            >
                <FormattedMessage
                    id={'components.gifts.loyaltyPage.termsAndConditionsLinkClickHere'}
                    defaultMessage={'click here'}
                />
            </Link>
        );
        return (
            <Row className={style.TsAndCs}>
                <FormattedMessage
                        id={'components.gifts.loyaltyPage.termsAndConditionsLink'}
                        defaultMessage={'All products are subject to availability, for full loyalty scheme terms & ' +
                            'conditions please {linkForWordsClickHere}.'}
                        values={{
                            linkForWordsClickHere
                        }}
                />
            </Row>
        );
    }

    private renderTermsAndConditionsPopup(): JSX.Element {
        return(
            <LoyaltyTermsAndConditionsModal acknowledgeLoyaltyTermsAndConditions={this.props.acknowledgeLoyaltyTermsAndConditions}/>
        );
    }
}

function mapStateToProps(
    state: AppState, ownProps: {}, getRequestError: (request: RequestClass<any>) => RequestStateProps
): LoyaltyPageStateProps {
    const giftRequestStateProps = getRequestError(GiftsRequest);
    const loyaltyTermsAndConditionsStateProps = getRequestError(LoyaltyTermsAndConditionsMetaDataRequest);
    return {
        isLoading: giftRequestStateProps.isLoading || loyaltyTermsAndConditionsStateProps.isLoading,
        categories: getCategorisedGifts(state.gifts),
        canAffordCategoryGifts: getCanAffordGifts(state.gifts),
        balance: state.gifts.balance,
        mustAcknowledgeTermsAndConditions: state.loyaltyTermsAndConditionsMetaData.read === false
    };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: {}): LoyaltyPageDispatchProps {
    return {
        fetchGifts: () => dispatch(new FetchRequest(new GiftsRequest())),
        fetchLoyaltyTermsAndConditionsMetaData: () => dispatch(new FetchRequest(new LoyaltyTermsAndConditionsMetaDataRequest())),
        acknowledgeLoyaltyTermsAndConditions: () => dispatch(new FetchRequest(new AcknowledgeLoyaltyTermsAndConditionsRequest()))
    };
}

export const LoyaltyPageContent = connectWithRequests(mapStateToProps, mapDispatchToProps)(injectIntl(LoyaltyPagePresentational));
